/**
 * @sahaj/websocket
 *
 * STOMP WebSocket client with exponential-backoff reconnect.
 * Platform-agnostic — usable by Web, Desktop (Tauri), and Mobile (React Native).
 *
 * Authentication: access token passed in STOMP CONNECT header.
 * Re-authenticated on every reconnect.
 * Subscription authorization: enforced server-side on every SUBSCRIBE frame.
 */

import { Client, type StompSubscription, type IMessage, type StompHeaders } from '@stomp/stompjs';
import { config } from '@sahaj/config';
import type { WebSocketEvent, WebSocketEventType } from '@sahaj/types';

// ─── Connection state ─────────────────────────────────────────────────────────

export type ConnectionState =
  | 'DISCONNECTED'
  | 'CONNECTING'
  | 'CONNECTED'
  | 'RECONNECTING'
  | 'FAILED';

// ─── Subscription topics ──────────────────────────────────────────────────────

export const topics = {
  workspace: (workspaceId: string) =>
    `/topic/workspace.${workspaceId}`,

  channel: (workspaceId: string, channelId: string) =>
    `/topic/workspace.${workspaceId}.channel.${channelId}`,

  dm: (workspaceId: string, conversationId: string) =>
    `/topic/workspace.${workspaceId}.dm.${conversationId}`,

  channelCall: (workspaceId: string, channelId: string) =>
    `/topic/workspace.${workspaceId}.channel.${channelId}.call`,

  notifications: () => '/user/queue/notifications',

  calls: () => '/user/queue/calls',

  alerts: () => '/user/queue/alerts',

  errors: () => '/user/queue/errors',

  workforceLiveState: (workspaceId: string) => `/topic/workforce/live-state/${workspaceId}`,
};

// ─── Event handler types ──────────────────────────────────────────────────────

export type EventHandler<T = any> = (event: T) => void;
export type ConnectionStateHandler = (state: ConnectionState) => void;
export type ErrorHandler = (error: string) => void;

// ─── SahajWebSocketClient ─────────────────────────────────────────────────────

export interface WebSocketClientOptions {
  /** Called to get a fresh access token before every connect/reconnect */
  getAccessToken: () => Promise<string>;
  /** Called when connection state changes */
  onStateChange?: ConnectionStateHandler;
  /** Called when the server sends an error frame */
  onError?: ErrorHandler;
}

export class SahajWebSocketClient {
  private client: Client | null = null;
  private state: ConnectionState = 'DISCONNECTED';
  private subscriptions = new Map<string, StompSubscription>();
  private eventHandlers = new Map<string, Set<EventHandler>>();
  private readonly options: WebSocketClientOptions;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stateChangeListeners = new Set<ConnectionStateHandler>();

  constructor(options: WebSocketClientOptions) {
    this.options = options;
  }

  onStateChange(handler: ConnectionStateHandler): () => void {
    this.stateChangeListeners.add(handler);
    return () => {
      this.stateChangeListeners.delete(handler);
    };
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  async connect(): Promise<void> {
    if (this.state === 'CONNECTED') return;

    return new Promise<void>((resolve, reject) => {
      let unsub: (() => void) | null = null;
      const timeout = setTimeout(() => {
        if (unsub) unsub();
        reject(new Error('WebSocket connection timeout'));
      }, 10000);

      unsub = this.onStateChange((state) => {
        if (state === 'CONNECTED') {
          clearTimeout(timeout);
          if (unsub) unsub();
          resolve();
        } else if (state === 'FAILED') {
          clearTimeout(timeout);
          if (unsub) unsub();
          reject(new Error('WebSocket connection failed'));
        }
      });

      if (this.state === 'DISCONNECTED') {
        this.setState('CONNECTING');
        this.doConnect().catch((err) => {
          clearTimeout(timeout);
          if (unsub) unsub();
          reject(err);
        });
      }
    });
  }

  disconnect(): void {
    this.clearReconnectTimer();
    this.subscriptions.clear();
    this.client?.deactivate();
    this.client = null;
    this.reconnectAttempts = 0;
    this.setState('DISCONNECTED');
  }

  /**
   * Subscribe to a topic.
   * Server-side validates authorization on every SUBSCRIBE frame.
   * @returns unsubscribe function
   */
  subscribe(topic: string, handler: EventHandler): () => void {
    // Register handler
    if (!this.eventHandlers.has(topic)) {
      this.eventHandlers.set(topic, new Set());
    }
    this.eventHandlers.get(topic)!.add(handler);

    // Subscribe via STOMP if connected
    if (this.state === 'CONNECTED' && this.client) {
      this.doSubscribe(topic);
    }

    return () => {
      const handlers = this.eventHandlers.get(topic);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.eventHandlers.delete(topic);
          this.unsubscribeTopic(topic);
        }
      }
    };
  }

  /**
   * Send a STOMP message to an application destination (/app/...)
   */
  send(destination: string, body: any): void {
    if (this.state !== 'CONNECTED' || !this.client) {
      console.warn('[SahajWS] Cannot send message: client not connected', destination);
      return;
    }
    this.client.publish({
      destination,
      body: JSON.stringify(body),
    });
  }

  getState(): ConnectionState {
    return this.state;
  }

  // ─── Internal: connect ───────────────────────────────────────────────────────

  private async doConnect(): Promise<void> {
    const accessToken = await this.options.getAccessToken();

    this.client = new Client({
      brokerURL: config.ws.url,
      connectHeaders: {
        Authorization: `Bearer ${accessToken}`,
      } satisfies StompHeaders,
      reconnectDelay: 0, // We handle reconnect manually

      onConnect: () => {
        this.reconnectAttempts = 0;
        this.setState('CONNECTED');
        this.resubscribeAll();
      },

      onDisconnect: () => {
        this.setState('DISCONNECTED');
      },

      onStompError: (frame) => {
        const message = frame.headers['message'] ?? 'Unknown STOMP error';
        this.options.onError?.(message);
        this.scheduleReconnect();
      },

      onWebSocketError: () => {
        this.setState('RECONNECTING');
        this.scheduleReconnect();
      },

      onWebSocketClose: () => {
        if (this.state !== 'DISCONNECTED') {
          this.setState('RECONNECTING');
          this.scheduleReconnect();
        }
      },
    });

    this.client.activate();
  }

  // ─── Internal: reconnect ────────────────────────────────────────────────────

  private scheduleReconnect(): void {
    if (this.state === 'DISCONNECTED') return;
    this.clearReconnectTimer();

    const { initialDelayMs, multiplier, maxDelayMs } = config.ws_reconnect;
    const delay = Math.min(
      initialDelayMs * Math.pow(multiplier, this.reconnectAttempts),
      maxDelayMs,
    );
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      if (this.state !== 'DISCONNECTED') {
        void this.reconnectWithFreshToken();
      }
    }, delay);
  }

  private async reconnectWithFreshToken(): Promise<void> {
    this.setState('RECONNECTING');
    // Deactivate old client cleanly
    await this.client?.deactivate();
    this.client = null;
    await this.doConnect();
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // ─── Internal: subscriptions ─────────────────────────────────────────────────

  private resubscribeAll(): void {
    for (const topic of this.eventHandlers.keys()) {
      this.doSubscribe(topic);
    }
  }

  private doSubscribe(topic: string): void {
    if (!this.client || this.state !== 'CONNECTED') return;
    if (this.subscriptions.has(topic)) return;

    const sub = this.client.subscribe(topic, (message: IMessage) => {
      this.handleMessage(topic, message);
    });
    this.subscriptions.set(topic, sub);
  }

  private unsubscribeTopic(topic: string): void {
    const sub = this.subscriptions.get(topic);
    sub?.unsubscribe();
    this.subscriptions.delete(topic);
  }

  private handleMessage(topic: string, message: IMessage): void {
    try {
      const event = JSON.parse(message.body) as WebSocketEvent;
      const handlers = this.eventHandlers.get(topic);
      if (handlers) {
        for (const handler of handlers) {
          handler(event);
        }
      }
    } catch {
      // Malformed event — log and continue
      console.error('[SahajWS] Failed to parse event on topic', topic, message.body);
    }
  }

  // ─── Internal: state ─────────────────────────────────────────────────────────

  private setState(state: ConnectionState): void {
    if (this.state === state) return;
    this.state = state;
    this.options.onStateChange?.(state);
    this.stateChangeListeners.forEach((listener) => {
      try {
        listener(state);
      } catch (err) {
        console.error('[SahajWS] Error in state listener', err);
      }
    });
  }
}

// Re-export types
export type { WebSocketEvent, WebSocketEventType };
